import type { SEOIntent } from "@/lib/seo-intents";

export type ReportIntentSlug =
  | "career-report"
  | "marriage-report"
  | "dosha-report"
  | "sade-sati-report"
  | "pitra-dosh-report"
  | "mahadasha-forecast"
  | "career-astrology-report"
  | "muhurat-report"
  | "dosha-remedy-plan"
  | "annual-forecast";

export interface ReportIntentConfig {
  slug: ReportIntentSlug;
  reportSlug: string;
  intent: SEOIntent;
  eyebrow: string;
  title: string;
  description: string;
  lead: string;
  checkpoints: string[];
  fit: string;
  inputSurface: "kundali" | "matching";
  offerHeadline: string;
  offerSubhead: string;
  collectInputsLabel: string;
  customerProblem: string;
  faq: { q: string; a: string }[];
}

export const REPORT_INTENT_PAGES: Record<ReportIntentSlug, ReportIntentConfig> = {
  "career-report": {
    slug: "career-report",
    reportSlug: "career-report",
    intent: "chart_report",
    eyebrow: "Career Report",
    title: "Why does your career keep stalling despite hard work?",
    description:
      "A personal Vedic career report for 10th house, D10/Dasamsa, Shani, Jupiter, dasha timing, wealth yogas, and career upay.",
    lead: "Career does not move only because effort increases. Your 10th house, D10, Shani, Jupiter, Sun, Budh, and active dasha decide when work becomes visible and when patience is smarter.",
    checkpoints: [
      "10th house and D10/Dasamsa career direction",
      "Current dasha and transit support for job or business",
      "Wealth yogas and blocked income patterns",
      "Career upay matched to the graha causing delay",
    ],
    fit: "Best when promotions, job changes, business timing, or income growth feel delayed despite consistent effort.",
    inputSurface: "kundali",
    offerHeadline: "Get your Career Report",
    offerSubhead:
      "Generate your kundali first, then get the 10th house, D10, dasha timing, wealth yogas, and career upay report matched to your chart.",
    collectInputsLabel: "Enter birth details for Career Report",
    customerProblem: "career",
    faq: [
      {
        q: "Will it tell me the right profession?",
        a: "It shows the work environments, strengths, timing, and career pressure points your chart supports instead of giving a generic job list.",
      },
      {
        q: "Does it include business guidance?",
        a: "Yes. It reads career and finance through the 10th, 11th, dasha, and relevant graha strength.",
      },
    ],
  },
  "marriage-report": {
    slug: "marriage-report",
    reportSlug: "marriage-report",
    intent: "chart_report",
    eyebrow: "Marriage Report",
    title: "Why does the same relationship doubt return before marriage?",
    description:
      "A family-shareable Marriage Report for Kundali Milan, 7th house, Mangal Dosh, D9/Navamsha, dasha timing, and practical upay.",
    lead: "Marriage matching should not stop at a score. The 7th house, Mangal placement, Nadi/Bhakoot, D9/Navamsha, and both partners' dasha timing decide whether the match is stable, delayed, or needs upay.",
    checkpoints: [
      "Full 36-point Gun Milan with the weak kootas explained",
      "Mangal Dosh, Nadi, Bhakoot, Gana, and cancellation checks",
      "D9/Navamsha and dasha timing for marriage stability",
      "Clear strengths, risks, remedies, and family-shareable summary",
    ],
    fit: "Best before families decide, when the match looks good but one chart point or repeated doubt needs a proper Vedic cross-check.",
    inputSurface: "matching",
    offerHeadline: "Get your Marriage Report",
    offerSubhead:
      "Enter both birth details first. The report uses the actual Kundali Milan result, Mangal Dosh, D9/Navamsha, and dasha timing before checkout.",
    collectInputsLabel: "Enter both birth details",
    customerProblem: "marriage",
    faq: [
      {
        q: "Why do you need both birth details?",
        a: "The paid Marriage Report is based on two charts. Without both birth details, Gun Milan, Mangal Dosh cross-check, D9/Navamsha, and dasha synchrony would be incomplete.",
      },
      {
        q: "Is this more than a Gun Milan score?",
        a: "Yes. The score is only the first layer. The report explains the weak areas, cancellations, dasha timing, relationship risks, and upay families can actually discuss.",
      },
    ],
  },
  "dosha-report": {
    slug: "dosha-report",
    reportSlug: "dosha-remedy-plan",
    intent: "remedy_dosha",
    eyebrow: "Dosha Report",
    title: "Why follow a dosha upay before checking your chart?",
    description:
      "A chart-specific Dosha Report for Mangal, Kaal Sarp, Pitra, Shani and other pressure checks, with severity, cancellation, and safe upay.",
    lead: "A dosha report should reduce fear, not create it. The right read checks whether a dosha actually fires, whether cancellation applies, which life area is affected, and which upay is safe before any strong remedy is suggested.",
    checkpoints: [
      "Mangal, Kaal Sarp, Pitra, Shani and other chart pressure checks",
      "Severity and cancellation before remedies",
      "Affected life areas separated clearly",
      "Safe upay routine: mantra, donation, vrata, shanti, or product guidance",
    ],
    fit: "Best when one or more doshas have been mentioned and you need the safe next step instead of a generic remedy list.",
    inputSurface: "kundali",
    offerHeadline: "Get your Dosha Report",
    offerSubhead:
      "Generate your kundali first, then get the dosha severity, cancellation checks, active life area, and safe upay sequence matched to your chart.",
    collectInputsLabel: "Enter birth details for Dosha Report",
    customerProblem: "dosha",
    faq: [
      {
        q: "Will it check multiple doshas?",
        a: "Yes. It focuses on the doshas available in the Ishvaram engine and explains which one is actually active for your chart.",
      },
      {
        q: "Will it recommend a strong ritual immediately?",
        a: "No. The report checks cancellation and severity first, then recommends the lighter or stronger upay your chart actually supports.",
      },
    ],
  },
  "sade-sati-report": {
    slug: "sade-sati-report",
    reportSlug: "sade-sati",
    intent: "remedy_dosha",
    eyebrow: "Sade Sati Report",
    title: "Is Shani testing your Moon sign, or is another dasha creating the pressure?",
    description:
      "A personal Sade Sati report for Shani phase, natal Saturn, dasha context, pressure areas, and chart-specific upay.",
    lead: "Sade Sati should not be read from Moon sign alone. Your natal Shani, active dasha, and house pressure decide whether this phase feels like delay, discipline, family pressure, career restructuring, or inner heaviness.",
    checkpoints: [
      "Current Sade Sati phase from your Moon sign",
      "Natal Shani placement and strength",
      "Dasha context so Shani is not blamed for everything",
      "Simple Shani upay matched to your chart",
    ],
    fit: "Best when you already know or suspect Sade Sati and want a chart-level answer before doing heavy Shani remedies.",
    inputSurface: "kundali",
    offerHeadline: "Get your Sade Sati Report",
    offerSubhead:
      "Generate your kundali first, then get Shani phase, natal Saturn, dasha context, pressure areas, and chart-specific upay.",
    collectInputsLabel: "Enter birth details for Sade Sati Report",
    customerProblem: "sade_sati",
    faq: [
      {
        q: "Will this tell me exact Sade Sati dates?",
        a: "It gives the phase context from your Moon sign and connects it with your kundali. Exact timing is computed after you submit birth details.",
      },
      {
        q: "Is it only for people currently in Sade Sati?",
        a: "No. It can also help if Sade Sati recently ended or is approaching, because the report reads Shani's natal promise and dasha context.",
      },
    ],
  },
  "pitra-dosh-report": {
    slug: "pitra-dosh-report",
    reportSlug: "pitra-dosh",
    intent: "remedy_dosha",
    eyebrow: "Pitra Dosh Report",
    title: "Is Pitra Dosh really present, or is your chart showing a different family pattern?",
    description:
      "A personal Pitra Dosh report for Sun, 9th house, Rahu/Ketu, cancellation checks, dasha activation, and safe ancestral upay.",
    lead: "Pitra Dosh is serious enough that it should be confirmed before any tarpan, shradh, donation, or puja sequence is started. The report checks the rule, severity, cancellation, and activation window first.",
    checkpoints: [
      "Sun, 9th house, 9th lord, Rahu/Ketu and Saturn checks",
      "Cancellation factors before severity is finalised",
      "Family, father-line, career, and money pattern interpretation",
      "Tarpan, shradh, mantra, donation, or lighter upay sequence",
    ],
    fit: "Best when recurring family or career blocks are being explained as ancestral karma and you want the chart verified first.",
    inputSurface: "kundali",
    offerHeadline: "Get your Pitra Dosh Report",
    offerSubhead:
      "Generate your kundali first, then get Sun, 9th house, Rahu/Ketu, cancellation checks, dasha activation, and safe ancestral upay.",
    collectInputsLabel: "Enter birth details for Pitra Dosh Report",
    customerProblem: "pitra_dosh",
    faq: [
      {
        q: "Is this different from the Pitra Dosh calculator?",
        a: "Yes. The calculator gives the quick signal. The report explains severity, cancellation, activation, affected life areas, and the upay sequence.",
      },
      {
        q: "Will it recommend an expensive ritual?",
        a: "Only if the chart supports a stronger upay. Mild or cancelled patterns are handled with lighter, safer upay.",
      },
    ],
  },
  "mahadasha-forecast": {
    slug: "mahadasha-forecast",
    reportSlug: "mahadasha-forecast",
    intent: "chart_report",
    eyebrow: "Mahadasha Forecast",
    title: "Which mahadasha is setting the timing behind your next 12 months?",
    description:
      "A 12-month Mahadasha Forecast with current mahadasha, antardasha, transition timing, decision windows, and graha-specific upay.",
    lead: "Mahadasha is the background climate of your life. The same effort can feel blocked or supported depending on the active graha and sub-period. This report turns that timing into practical next steps.",
    checkpoints: [
      "Current mahadasha and antardasha from Moon nakshatra",
      "Next transition points and month-level timing",
      "Career, money, marriage, family, health, and spiritual focus",
      "Graha-specific upay for the active period",
    ],
    fit: "Best when you are deciding whether to act, wait, change job, start something, or understand why a phase has shifted.",
    inputSurface: "kundali",
    offerHeadline: "Get your Mahadasha Forecast",
    offerSubhead:
      "Generate your kundali first, then get current mahadasha, antardasha, transition timing, decision windows, and graha-specific upay.",
    collectInputsLabel: "Enter birth details for Mahadasha Forecast",
    customerProblem: "mahadasha",
    faq: [
      {
        q: "Is this the same as Varshphal?",
        a: "No. Varshphal reads the year chart. This report reads your active dasha period and the next 12 months of sub-period movement.",
      },
      {
        q: "Can I use it without exact birth time?",
        a: "Use the most accurate time available. The dasha comes from Moon nakshatra, while exact birth time improves the house context.",
      },
    ],
  },
  "career-astrology-report": {
    slug: "career-astrology-report",
    reportSlug: "career-report",
    intent: "chart_report",
    eyebrow: "Career Astrology Report",
    title: "Why does your hard work not convert into career results at the right time?",
    description:
      "A Vedic career astrology report for 10th house, D10/Dasamsa, Shani, Jupiter, dasha timing, wealth yogas, and career upay.",
    lead: "Career timing is not only about effort. The 10th house, D10, Shani, Jupiter, Sun, Mercury, and the active dasha decide when work becomes visible and when patience is smarter.",
    checkpoints: [
      "10th house and Dasamsa career direction",
      "Current dasha and transit support for job or business",
      "Wealth yogas and blocked income patterns",
      "Career upay matched to the graha causing delay",
    ],
    fit: "Best when promotions, job changes, business timing, or income growth feel delayed despite consistent effort.",
    inputSurface: "kundali",
    offerHeadline: "Get your Career Report",
    offerSubhead:
      "Generate your kundali first, then get the 10th house, D10, dasha timing, wealth yogas, and career upay report matched to your chart.",
    collectInputsLabel: "Enter birth details for Career Report",
    customerProblem: "career",
    faq: [
      {
        q: "Will it tell me the right profession?",
        a: "It shows the work environments, strengths, and timing your chart supports instead of giving a generic job list.",
      },
      {
        q: "Does it include business guidance?",
        a: "Yes. It reads career and finance through the 10th, 11th, dasha, and relevant graha strength.",
      },
    ],
  },
  "muhurat-report": {
    slug: "muhurat-report",
    reportSlug: "muhurat-report",
    intent: "timing_muhurat",
    eyebrow: "Personal Muhurat Report",
    title: "Why does a shubh time need your city, purpose, and kundali together?",
    description:
      "A personal Muhurat report for purpose-specific timing, avoid windows, panchang, sankalp, and ritual checklist.",
    lead: "A public choghadiya slot is only the outer layer. A cleaner muhurat checks city panchang, purpose, Moon sign, dasha pressure, avoid windows, and the sankalp you should take before starting.",
    checkpoints: [
      "Purpose-specific timing instead of a generic table",
      "Tithi, nakshatra, yoga, karana, weekday, and choghadiya",
      "Rahu Kaal, Yamagandam, Gulika Kaal, and personal dasha pressure",
      "Sankalp wording and practical ritual checklist",
    ],
    fit: "Best before launches, purchases, griha pravesh, travel, naming, rituals, or family decisions where timing matters.",
    inputSurface: "kundali",
    offerHeadline: "Get your Muhurat Report",
    offerSubhead:
      "Generate your kundali first, then get purpose-specific timing, avoid windows, panchang, sankalp, and ritual checklist.",
    collectInputsLabel: "Enter details for Muhurat Report",
    customerProblem: "muhurat",
    faq: [
      {
        q: "Is this better than a choghadiya calculator?",
        a: "It uses the public timing layer but adds your purpose, city, birth chart, avoid windows, and sankalp.",
      },
      {
        q: "Can it replace a wedding muhurat?",
        a: "Use it for initial timing guidance. A final wedding muhurat still benefits from family constraints and pandit confirmation.",
      },
    ],
  },
  "annual-forecast": {
    slug: "annual-forecast",
    reportSlug: "annual-forecast",
    intent: "chart_report",
    eyebrow: "Annual Forecast 2026-27",
    title: "Why will some months in the year ahead lift you and others test you?",
    description:
      "A personal Annual Forecast (varshphal) for the year ahead - month-by-month dasha and transit timing, the windows that favour you, and the ones to plan around.",
    lead: "A generic yearly rashifal reads the same for millions. Your varshphal is built from your own chart - the running mahadasha and antardasha, this year's Shani and Jupiter transits over your houses, and the specific months they turn supportive or heavy.",
    checkpoints: [
      "Month-by-month timing for the next 12 months, not a generic yearly blurb",
      "Your running mahadasha and antardasha and what each is ripening",
      "Shani (Saturn) and Jupiter transits over your houses, with dates",
      "The strong windows to act and the heavy windows to plan around",
    ],
    fit: "Best when you want to time the year ahead - career moves, money decisions, marriage, travel, or major commitments - to your own chart.",
    inputSurface: "kundali",
    offerHeadline: "Get your Annual Forecast",
    offerSubhead:
      "Generate your kundali first, then get your month-by-month dasha and transit timing for the year ahead.",
    collectInputsLabel: "Enter details for Annual Forecast",
    customerProblem: "forecast",
    faq: [
      {
        q: "How is this different from a free yearly rashifal?",
        a: "A yearly rashifal is written for a whole rashi. This is computed from your exact birth chart - your dasha, your transits, your houses - so the timing is yours, not a sign's.",
      },
      {
        q: "Which year does it cover?",
        a: "The 12 months ahead from your reading, mapped to your running dasha and the live Shani and Jupiter transits.",
      },
    ],
  },
  "dosha-remedy-plan": {
    slug: "dosha-remedy-plan",
    reportSlug: "dosha-remedy-plan",
    intent: "remedy_dosha",
    eyebrow: "Dosha Remedy Plan",
    title: "Why choose an upay before confirming the dosha and cancellation?",
    description:
      "A chart-specific Dosha Remedy Plan for severity, cancellation, affected life area, safe upay, mantra routine, and matched remedy guidance.",
    lead: "A dosha report should reduce fear, not create it. This plan checks whether a dosha actually fires, whether cancellation applies, and which upay is safe before any strong remedy is suggested.",
    checkpoints: [
      "Mangal, Kaal Sarp, Pitra, Shani and other chart pressure checks",
      "Severity and cancellation before remedies",
      "Affected life areas separated clearly",
      "Safe upay routine: mantra, donation, vrata, shanti, or product guidance",
    ],
    fit: "Best when one or more doshas have been mentioned and you need the safe next step instead of a generic remedy list.",
    inputSurface: "kundali",
    offerHeadline: "Get your Dosha Remedy Plan",
    offerSubhead:
      "Generate your kundali first, then get dosha severity, cancellation checks, affected life area, and safe upay guidance.",
    collectInputsLabel: "Enter birth details for Dosha Plan",
    customerProblem: "dosha",
    faq: [
      {
        q: "Will it check multiple doshas?",
        a: "Yes. It focuses on the doshas available in the Ishvaram engine and explains which one is actually active for your chart.",
      },
      {
        q: "Does it recommend products?",
        a: "Only where the chart supports a product upay. The report prioritises safe routine and classical remedy order first.",
      },
    ],
  },
};
